import { useState, type ReactElement } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { resolveBuildRunNumber } from '@/features/builds/utils/buildRunNumber';
import { StageRunExpandedJobRuns } from '@/features/pipelines/components/PipelineTableV2NestedJobRuns';
import {
  resolveStageRunJobCount,
  resolveStageRunLinks,
} from '@/features/pipelines/components/PipelineTableV2NestedRuns.helpers';
import { StageExecutionEditDialog } from '@/features/pipelines/components/StageExecutionEditDialog';
import {
  useDeletePipelineStageExecution,
  useUpdatePipelineStageExecution,
} from '@/features/pipelines/hooks/pipelines';
import {
  buildOperationPassRateCountsFromStatusCounts,
  buildOperationPassRateCountsFromStatusSummaries,
} from '@/features/shared/components/tableSystem/operationPassRateCounts';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import { TableV2DurationCell } from '@/features/shared/components/tableSystem/TableV2DurationCell';
import {
  buildTableV2NestedBodyRowSx,
  tableV2NestedCarrierRowSx,
  type TableV2NestedTableDepth,
} from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2ChevronButton,
  TableV2DateTimeCell,
  TableV2NameCell,
  TableV2PassRateCell,
  TableV2StatusChip,
  TableV2TagGroup,
  tableV2ActionsColumnWidth,
  tableV2ChevronCellSx,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { resolveStageExecutionDisplayStatus } from '@/features/shared/utils/ciStatus';
import { useNotifications } from '@/providers/notificationHooks';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getToastMessage } from '@/shared/copy/toasts';
import { useApiErrorMessage } from '@/shared/data/errors';
import { s } from '@/theme/tokens';
import { TableCell, TableRow } from '@/ui/primitives';
import type { TagColorByName } from '@/features/shared/tags';
import type { PipelineStageExecutionListItemDto } from '@flowqa/shared';

export const StageRunNestedRow = ({
  depth = 2,
  isLastVisibleRow,
  organisationId,
  row,
  projectId,
  showStageLink,
  tagColorByName,
}: {
  depth?: TableV2NestedTableDepth;
  isLastVisibleRow: boolean;
  organisationId: string | null;
  row: PipelineStageExecutionListItemDto;
  projectId: string;
  showStageLink: boolean;
  tagColorByName: TagColorByName;
}): ReactElement => {
  const [expanded, setExpanded] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { notify } = useNotifications();
  const toErrorMessage = useApiErrorMessage();
  const updateStageExecution = useUpdatePipelineStageExecution();
  const deleteStageExecution = useDeletePipelineStageExecution();
  const jobCount = resolveStageRunJobCount(row);
  const runNumber = resolveBuildRunNumber(row.buildKey ?? null);
  const rowName = row.stageName;
  const runLabel = runNumber === null ? (row.buildKey ?? row.id) : `#${String(runNumber)}`;
  const links = resolveStageRunLinks(row);

  return (
    <>
      <TableRow
        sx={buildTableV2NestedBodyRowSx({
          depth,
          hasRenderedDivider: !expanded && !isLastVisibleRow,
        })}
      >
        <TableCell align="center" sx={tableV2ChevronCellSx}>
          <TableV2ChevronButton
            expanded={expanded}
            label={expanded ? 'Collapse job runs' : 'Expand job runs'}
            onClick={() => {
              setExpanded((current) => !current);
            }}
          />
        </TableCell>
        <TableCell>
          <TableV2NameCell
            href={showStageLink ? links.stageHref : links.executionHref}
            primary={showStageLink ? row.stageName : runLabel}
            secondary={showStageLink ? runLabel : null}
            secondaryHref={showStageLink ? links.executionHref : null}
          />
        </TableCell>
        <TableCell>
          <TableV2StatusChip status={resolveStageExecutionDisplayStatus(row)} />
        </TableCell>
        <TableCell>
          <TableV2PassRateCell
            counts={
              row.jobStatusCounts
                ? buildOperationPassRateCountsFromStatusCounts(row.jobStatusCounts)
                : buildOperationPassRateCountsFromStatusSummaries(row.jobSummaries)
            }
          />
        </TableCell>
        <TableCell>
          <TableV2DurationCell value={row.durationMs} />
        </TableCell>
        <TableCell>
          <TableV2DateTimeCell value={row.startedAt ?? row.lastUpdatedAt} />
        </TableCell>
        <TableCell>
          <TableV2TagGroup tagColorByName={tagColorByName} tags={row.tags ?? []} />
        </TableCell>
        <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
          <TableActionsCell
            rowName={rowName}
            verticalAlign="center"
            editActionLabel={getEntityActionLabel('edit', 'Stage Run')}
            deleteActionLabel={getEntityActionLabel('delete', 'Stage Run')}
            showEditTagsAction={false}
            onEdit={() => {
              setEditOpen(true);
            }}
            onDelete={() => {
              setDeleteOpen(true);
            }}
            disableEdit={updateStageExecution.isPending || deleteStageExecution.isPending}
            disableDelete={deleteStageExecution.isPending}
          />
        </TableCell>
      </TableRow>
      <TableRow
        sx={[
          tableV2NestedCarrierRowSx,
          expanded && !isLastVisibleRow
            ? { '& > td': { borderBottom: (theme) => `1px solid ${theme.app.colors.border}` } }
            : {},
        ]}
      >
        <TableCell colSpan={8}>
          <StageRunExpandedJobRuns
            childCount={jobCount}
            depth={depth === 2 ? 3 : 2}
            expanded={expanded}
            organisationId={organisationId}
            pipelineExecutionId={row.pipelineExecutionId}
            projectId={projectId}
            stageName={row.stageName}
            stageExecutionId={row.id}
            tagColorByName={tagColorByName}
          />
        </TableCell>
      </TableRow>
      <StageExecutionEditDialog
        open={editOpen}
        execution={row}
        loading={updateStageExecution.isPending}
        onClose={() => {
          if (updateStageExecution.isPending) return;
          setEditOpen(false);
        }}
        onSave={async ({ status, startedAt, finishedAt, durationMs }) => {
          await updateStageExecution.mutateAsync({
            id: row.id,
            projectId: row.projectId,
            status,
            startedAt,
            finishedAt,
            durationMs,
          });
          notify({ message: 'Stage execution updated', tone: 'green' });
          setEditOpen(false);
        }}
      />
      <ConfirmDialog
        open={deleteOpen}
        title={getDialogMessage('destructive.delete.title', { entity: 'stageExecution' })}
        description="This stage execution will be removed from the stage execution history view."
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'stageExecution',
        })}
        confirmColor="red"
        loading={deleteStageExecution.isPending}
        onClose={() => {
          if (deleteStageExecution.isPending) return;
          setDeleteOpen(false);
        }}
        onConfirm={() => {
          deleteStageExecution.mutate(
            { id: row.id, projectId: row.projectId },
            {
              onSuccess: () => {
                notify({
                  message: getToastMessage('entity.deleted', {
                    entity: 'stage execution',
                    name: row.stageName,
                  }),
                  tone: 'green',
                });
                setDeleteOpen(false);
              },
              onError: (error) => {
                notify({ message: toErrorMessage(error), tone: 'red' });
              },
            },
          );
        }}
      />
    </>
  );
};
